import { YOUTUBE_LINK_REGEXP } from 'pages/youtube-link-page/constants';
import { EAdminRoles } from 'shared/types';
import * as Yup from 'yup';

const FIELDS = {
  email: Yup.string()
    .email('Please enter a valid e-mail address')
    .max(254, 'Email must be 254 characters or fewer')
    .required('Required field'),
  strictEmail: Yup.string()
    .email('Please enter a valid e-mail address')
    .matches(/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/, 'Please enter a valid e-mail address')
    .max(254, 'Email must be 254 characters or fewer')
    .required('Required field'),
  emailLocalPart: Yup.string()
    .trim()
    .max(64, 'Must be 64 characters or fewer')
    .required('Required field'),
  tag: Yup.string().trim().required('Required field'),
  password: Yup.string()
    .min(8, 'Use 8 or more characters')
    .max(50, 'Only 50 symbols allowed')
    .required('Required field'),
  confirmPassword: Yup.string()
    .min(8, 'Use 8 or more characters')
    .max(50, 'Only 50 symbols allowed')
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Passwords must match'),
  name: Yup.string().trim().required('Required field'),
  personName: Yup.string()
    .trim()
    .max(100, 'Must be 100 characters or fewer')
    .required('Required field'),
  nameNotRequired: Yup.string().trim(),
  userIdRange: Yup.object().nullable(),
  firebaseId: Yup.string()
    .required('Required field')
    .test('validId', 'Must be exactly 4 digits', (value) => /^\d{4}$/.test(value || '')),
  role: Yup.string().required('Required field'),
  school: Yup.string().when('role', (role, schema) => {
    const roleValue = Array.isArray(role) ? role[0] : role;
    return roleValue === EAdminRoles.SCHOOL_ADMIN ? schema.required('Required field') : schema;
  }),
  code: Yup.string()
    .min(4, 'Use 4 or more characters')
    .max(10, 'Only 10 symbols allowed')
    // .required('Required field')
    ,
  schoolPassword: Yup.string()
    .min(4, 'Use 4 or more characters')
    .max(64, 'Only 64 symbols allowed')
    // Not trimmed on purpose: the Cloud Function hashes the password exactly as sent,
    // so a padded value here would be impossible to reproduce on a VR keyboard.
    .test(
      'no-edge-whitespace',
      'Cannot start or end with a space',
      (value) => !value || value === value.trim()
    )
    .required('Required field'),
  confirmSchoolPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Required field'),
  youtubeName: Yup.string().trim().required('Required field'),
  youtubeLink: Yup.string()
    .trim()
    .required('Required field')
    .matches(YOUTUBE_LINK_REGEXP, 'Link must be a valid YouTube URL')
};

const domainRegex = /^(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

export const validation = {
  LOG_IN: Yup.object().shape({
    email: FIELDS.email,
    password: FIELDS.password
  }),
  SET_SCHOOL_PASSWORD: Yup.object().shape({
    password: FIELDS.schoolPassword,
    confirmPassword: FIELDS.confirmSchoolPassword
  }),
  ADD_SCHOOL: Yup.object().shape({
    Name: Yup.string()
      .trim()
      .max(255, 'School name must be 255 characters or fewer')
      .required('Required field'),
    Domain: Yup.array()
      .transform((value, originalValue) => {
        if (typeof originalValue === 'string') {
          return originalValue
            .split(/ *, */)
            .map(v => v.trim())
            .filter(Boolean);
        }
        return value;
      })
      .min(1, 'Required field')
      .test('valid-domains', 'Invalid domain format (expected: "example.com")', (value) => {
        if (!value || !Array.isArray(value)) return false;
        return value.every(domain => domainRegex.test(domain));
      })
      .test('domain-length', 'Each domain must be 253 characters or fewer', (value) => {
        if (!value || !Array.isArray(value)) return true;
        return value.every(domain => domain.length <= 253);
      })
      .required('Required field'),
    Abbreviation: Yup.string()
      .trim()
      .max(50, 'Abbreviation must be 50 characters or fewer')
      .required('Required field')
  }),
  UPLOAD_SCHOOL_NAME: Yup.object().shape({
    name: FIELDS.name
  }),
  CREATE_ADMIN: Yup.object().shape({
    email: FIELDS.strictEmail,
    password: FIELDS.password,
    firstName: FIELDS.personName,
    lastName: FIELDS.personName,
    school: FIELDS.school,
    role: FIELDS.role
  }),
  EDIT_ADMIN: Yup.object().shape({
    email: FIELDS.strictEmail,
    firstName: FIELDS.personName,
    lastName: FIELDS.personName,
    school: FIELDS.school,
    role: FIELDS.role
  }),
  RESET_PASSWORD: Yup.object().shape({
    email: FIELDS.email
  }),
  CHANGE_ADMIN_PASSWORD: Yup.object().shape({
    password: FIELDS.password,
    confirmPassword: FIELDS.confirmPassword
  }),
  CHANGE_ADMIN_TAG: Yup.object().shape({
    tag: FIELDS.tag
  }),
  CREATE_VR_USER: Yup.object().shape({
    firstName: FIELDS.personName,
    lastName: FIELDS.personName,
    emailName: FIELDS.emailLocalPart,
    email: FIELDS.email,
    // Code: FIELDS.firebaseId
  }),
  UPDATE_VR_USER: Yup.object().shape({
    firstName: FIELDS.personName,
    lastName: FIELDS.personName,
    emailName: FIELDS.emailLocalPart,
    email: FIELDS.email,
    // Code: FIELDS.firebaseId
  }),
  CREATE_YOUTUBE_LINK: Yup.object().shape({
    name: FIELDS.youtubeName,
    link: FIELDS.youtubeLink
  })
};
